/**
 * FBP F2 — an employee's own declaration of how their CTC template's flexi pool (ADR-style:
 * `CtcTemplateLine.flexiPool`, FBP F1) is allocated across the flexi-eligible components
 * (`SalaryComponent.isFlexi`) their template offers, for one fiscal year.
 *
 * A DIFFERENT THING FROM `payroll-tax.contract.ts`'s income-tax declarations (80C, HRA): those are
 * evidence claims with a verification workflow; this is a compensation-structuring choice capped by
 * the pool amount, with nothing to verify — `FbpDeclaration`'s own schema comment explains why it is
 * a separate model. `/me/*` here needs `payroll.fbp.own:manage` at self scope, same self-only shape
 * as `payroll.tax.own:manage`, but it is its own permission: holding one must never imply the other.
 *
 * NO HR/ADMIN SURFACE YET. Reading or overriding someone else's declaration, and locking one once a
 * payroll run consumes it, are F6/F7 — this phase is the employee's own read/declare/submit.
 *
 * Money is a decimal string with its currency alongside (invariant 2).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  uuidSchema,
} from '../schemas/common.js';
import { PAYROLL_NATURE_KEY_PATTERN } from './payroll-compensation.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();
const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const fbpDeclarationStatusSchema = z.enum(['draft', 'submitted', 'locked']);
export type FbpDeclarationStatus = z.infer<typeof fbpDeclarationStatusSchema>;

/** One flexi head within the declaration — eligible whether or not the employee has put money against it yet. */
export const fbpDeclarationLineSchema = z.object({
  componentId: uuidSchema,
  componentCode: z.string(),
  componentName: z.string(),
  /** Copied from the component catalogue at read time (FBP F1) — null means no statutory/policy cap. */
  flexiAnnualLimit: decimalStringSchema.nullable(),
  flexiExemptionRule: z.string().regex(PAYROLL_NATURE_KEY_PATTERN).nullable(),
  /** What the employee has put against this head. Zero, not omitted, for a head not yet allocated. */
  declaredAmount: decimalStringSchema,
});
export type FbpDeclarationLine = z.infer<typeof fbpDeclarationLineSchema>;

export const myFbpDeclarationSchema = z.object({
  id: uuidSchema,
  fiscalYear: z.number().int(),
  status: fbpDeclarationStatusSchema,
  currency: z.string(),
  /** The flexi pool's total annual amount, snapshotted when the declaration was opened. Null if the
   *  employee's template offers no flexi pool lines at all — the declaration still exists, empty. */
  poolAmount: decimalStringSchema.nullable(),
  totalDeclared: decimalStringSchema,
  lines: z.array(fbpDeclarationLineSchema),
  submittedAt: z.string().nullable(),
  version: z.number().int(),
});
export type MyFbpDeclaration = z.infer<typeof myFbpDeclarationSchema>;

const fiscalYearQuerySchema = z.object({ fiscalYear: z.coerce.number().int().optional() });

export const upsertFbpDeclarationLineBodySchema = z.object({
  declaredAmount: decimalStringSchema,
  expectedVersion: z.number().int(),
});

export const submitFbpDeclarationBodySchema = z.object({
  expectedVersion: z.number().int(),
});

export const payrollFbpDeclarationContract = c.router(
  {
    getMyDeclaration: {
      method: 'GET',
      path: '/me',
      query: fiscalYearQuerySchema,
      responses: { 200: myFbpDeclarationSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: "Read (opening a draft if none exists) the caller's own FBP declaration",
      metadata: routeMeta('payroll.fbp.own:manage'),
    },

    upsertMyDeclarationLine: {
      method: 'PUT',
      path: '/me/lines/:componentId',
      pathParams: z.object({ componentId: uuidSchema }),
      body: upsertFbpDeclarationLineBodySchema,
      responses: {
        200: myFbpDeclarationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** The line's amount would push totalDeclared past the pool, or the declaration is locked. */
        422: errorBodySchema,
      },
      summary: "Set one flexi head's declared amount on the caller's own declaration",
      metadata: routeMeta('payroll.fbp.own:manage'),
    },

    submitMyDeclaration: {
      method: 'POST',
      path: '/me/submit',
      headers: idempotencyHeadersSchema,
      body: submitFbpDeclarationBodySchema,
      responses: {
        200: myFbpDeclarationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "Submit the caller's own FBP declaration for the fiscal year",
      metadata: routeMeta('payroll.fbp.own:manage'),
    },
  },
  { pathPrefix: '/payroll/fbp/declarations' },
);
